import { BYE } from "../../../publicFunctions/adminDuelHandler";
import "../../../styles/tournamentView.css";
import { useEffect, useState, useRef } from "react";
import {
  createLinesToNextMatch,
  getCellPosition,
  getCellSizeForLane,
  getCenteredObjectPosition,
  getLaneBackgroundColor,
  getNumberOfMatchesBottomBracket,
  getPseudoLaneNumber,
  getPseudoMatchNumber,
  getSizesForZoomLevel,
  isThisLaneLeftSide,
  numberOfMatchesForLane,
  TBD,
} from "../../../publicFunctions/publicHelpers/tournamentViewHelper";
import { addMatchToPayload } from "./addMatchToPayload";
import TournamentLaneHeader from "./TournamentLaneHeader";
import TournamentMiniMap from "./TournamentMiniMap";
import TournamentZoomControls from "./TournamentZoomControls";

function FSTournamentView({
  mutedUsers,
  finalShowdownFromServer,
  storyListFromServer,
  cardImageUrl,
  genreText,
  nightMode,
  debriefMeta,
}) {
  // Determine default zoom level based on screen size
  const getDefaultZoomLevel = () => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768 ? 3 : 2; // 768px is typical tablet breakpoint
    }
    return 2; // Default fallback
  };

  const [zoomLevel, setZoomLevel] = useState(getDefaultZoomLevel());
  const [finalShowdown, setFinalShowdown] = useState(null);
  const [renderedView, setRenderedView] = useState(null);
  const [currentViewport, setCurrentViewport] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const tournamentRef = useRef(null);
  // Add a ref to store the focus point in bracket coordinates
  const focusPointRef = useRef(null);
  // Add isZooming state
  const [isZooming, setIsZooming] = useState(false);
  // Add dragging state for click-and-drag panning
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  // Add state to ensure we only center once on load
  const [hasCenteredOnLoad, setHasCenteredOnLoad] = useState(false);

  // Track viewport changes from scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (tournamentRef.current && finalShowdown) {
        const { scrollLeft, scrollTop, clientWidth, clientHeight } =
          tournamentRef.current;
        setCurrentViewport({
          x: scrollLeft,
          y: scrollTop,
          width: clientWidth,
          height: clientHeight,
        });
      }
    };

    const tournamentElement = tournamentRef.current;
    if (tournamentElement) {
      tournamentElement.addEventListener("scroll", handleScroll);
      // Set initial viewport
      handleScroll();
      return () =>
        tournamentElement.removeEventListener("scroll", handleScroll);
    }
  }, [finalShowdown]);

  // Handle viewport change from mini map clicks
  const handleViewportChange = (newViewport) => {
    setCurrentViewport(newViewport);
    if (tournamentRef.current) {
      tournamentRef.current.scrollTo({
        left: newViewport.x,
        top: newViewport.y,
        behavior: "smooth",
      });
    }
  };

  // new features
  // create the working finalShowdown object.
  // If there is no round 2, then we need to create round 2 for
  // matches with Byes in round 1
  useEffect(() => {
    if (
      finalShowdownFromServer?.totalRounds &&
      storyListFromServer?.length &&
      debriefMeta
    ) {
      const tempStoryMap = new Map();
      for (let i = 0; i < storyListFromServer.length; i++) {
        const story = storyListFromServer[i];
        tempStoryMap.set(story.storyId, story);
      }

      const houseIdToHouseInfo = new Map();
      for (let i = 1; i <= 4; i++) {
        const houseForThisGenre = debriefMeta.houses["genre" + i.toString(10)];
        for (let j = 0; j < houseForThisGenre.length; j++) {
          const house = houseForThisGenre[j];
          houseIdToHouseInfo.set(house.houseId, house);
        }
      }

      const totalRounds = finalShowdownFromServer.totalRounds;
      const sizes = getSizesForZoomLevel(zoomLevel, totalRounds);
      // create arrayOfArray for the lanes and matches
      const lanes = [];
      const numberOfLanes = sizes.numberOfLanes;
      for (let lane = 1; lane <= numberOfLanes; lane++) {
        const matches = [];
        const numMatchForLane = numberOfMatchesForLane(
          lane,
          totalRounds,
          getNumberOfMatchesBottomBracket(totalRounds),
          numberOfLanes
        );
        for (let j = 0; j < numMatchForLane; j++) {
          matches.push({
            topScore: 0,
            topStoryId: TBD,
            topScore: 0,
            bottomStoryId: TBD,
            bottomScore: 0,
            winner: "",
          });
        }
        lanes.push(matches);
      }

      const projectedRound2 = [];
      // We need to create a projected round 2 if there are Byes
      if (finalShowdownFromServer.currentRound === 0) {
        const round1 = finalShowdownFromServer.rounds.round1;
        for (let i = 0; round1 && i < round1.length; i += 2) {
          const matchLeft = round1[i];
          const matchRight = round1[i + 1];

          let leftStoryId = "";
          if (matchLeft.leftStoryId === BYE) {
            leftStoryId = matchLeft.rightStoryId;
          } else if (matchLeft.rightStoryId === BYE) {
            leftStoryId = matchLeft.leftStoryId;
          }
          let rightStoryId = "";
          if (matchRight.leftStoryId === BYE) {
            rightStoryId = matchRight.rightStoryId;
          } else if (matchRight.rightStoryId === BYE) {
            rightStoryId = matchRight.leftStoryId;
          }
          projectedRound2.push({
            leftStoryId,
            rightStoryId,
            leftScore: 0,
            rightScore: 0,
            winner: "",
          });
        }
      }

      for (let i = 1; i <= totalRounds; i++) {
        let round;
        if (i <= finalShowdownFromServer.currentRound + 1) {
          round = finalShowdownFromServer.rounds["round" + i.toString(10)];
        } else if (i === 2 && projectedRound2?.length) {
          round = projectedRound2;
        } else {
          break;
        }
        if (!round) {
          break;
        }

        // Okay, we have a round!
        for (
          let realMatchIndex = 0;
          realMatchIndex < round.length;
          realMatchIndex++
        ) {
          let leftIsTop = realMatchIndex < Math.round(round.length / 2);
          const pseudoLane = getPseudoLaneNumber(leftIsTop, i, totalRounds);

          const laneIndexToUse = pseudoLane - 1;
          const numMatchesLane = numberOfMatchesForLane(
            pseudoLane,
            totalRounds,
            sizes.numberOfMatchesBottomBracket,
            sizes.numberOfLanes
          );
          const matchIndexToUse = getPseudoMatchNumber(
            leftIsTop,
            realMatchIndex,
            numMatchesLane,
            round.length,
            totalRounds % 2 !== 0
          );

          const matchFromServer = round[realMatchIndex];
          const match = lanes[laneIndexToUse][matchIndexToUse];
          if (!match) {
            // console.log(lanes[laneIndexToUse]);
            // console.log(matchFromServer);
            // console.log("index " + j);
            // console.log("numMatchesLane " + numMatchesLane);
            // console.log("laneIndexToUse " + laneIndexToUse);
            // console.log("matchIndexToUse " + matchIndexToUse);
            // console.log("pseudoLane " + pseudoLane);
          }

          if (i === totalRounds) {
            // center
            leftIsTop = true;
          }

          match.topScore = leftIsTop
            ? matchFromServer.leftScore
            : matchFromServer.rightScore;
          match.bottomScore = leftIsTop
            ? matchFromServer.rightScore
            : matchFromServer.leftScore;
          match.topStoryId = leftIsTop
            ? matchFromServer.leftStoryId
            : matchFromServer.rightStoryId;
          match.bottomStoryId = leftIsTop
            ? matchFromServer.rightStoryId
            : matchFromServer.leftStoryId;
          match.winner = matchFromServer.winner;
        }
      }

      setFinalShowdown({
        storyMap: tempStoryMap,
        sizes,
        lanes,
        houseIdToHouseInfo,
      });
    }
  }, [finalShowdownFromServer, storyListFromServer, debriefMeta, zoomLevel]);

  // now that we have the finalShowdown object, we should render it
  useEffect(() => {
    if (finalShowdown) {
      const sizes = finalShowdown.sizes;
      const tempPayload = [];
      let matchBoxClass = nightMode ? "matchBox--white" : "matchBox";
      if (zoomLevel > 2) {
        if (nightMode) {
          matchBoxClass += " matchBox--white--thin";
        } else {
          matchBoxClass += " matchBox--thin";
        }
      }

      // Lane backgrounds
      const laneBackgrounds = [];

      // Create cells
      for (let lane = 1; lane <= finalShowdown.sizes.numberOfLanes; lane++) {
        const bgColor = getLaneBackgroundColor({
          lane,
          totalLanes: sizes.numberOfLanes,
          sizes,
          nightMode,
        });
        laneBackgrounds.push(
          <div
            key={"lane-bg-" + lane}
            style={{
              position: "absolute",
              left: (lane - 1) * sizes.laneW + "px",
              top: 0,
              width: sizes.laneW + "px",
              height: sizes.screenSizeH + sizes.headerHeight + "px",
              background: bgColor,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        );

        const numOfMatches = numberOfMatchesForLane(
          lane,
          sizes.totalRounds,
          sizes.numberOfMatchesBottomBracket,
          sizes.numberOfLanes
        );
        const cellSize = getCellSizeForLane(sizes, numOfMatches);

        let redOrBlue = 1;
        for (let row = 0; row < numOfMatches; row++, redOrBlue *= -1) {
          const cellPos = getCellPosition(cellSize, row, lane);
          const match = finalShowdown.lanes[lane - 1][row];

          // actual match
          const centeredMatchPos = getCenteredObjectPosition(
            cellPos,
            cellSize,
            sizes.matchBoxW,
            sizes.matchBoxH
          );

          // START OF LINES ********************
          const isLaneOnLeft = isThisLaneLeftSide(lane, sizes.numberOfLanes);
          if (lane !== Math.ceil(sizes.numberOfLanes / 2)) {
            // next match lookahead
            const nextCellSize = getCellSizeForLane(
              sizes,
              numberOfMatchesForLane(
                isLaneOnLeft ? lane + 1 : lane - 1,
                sizes.totalRounds,
                sizes.numberOfMatchesBottomBracket,
                sizes.numberOfLanes
              )
            );
            const centeredNextMatchPos = getCenteredObjectPosition(
              getCellPosition(
                nextCellSize,
                Math.floor(row / 2),
                isLaneOnLeft ? lane + 1 : lane - 1
              ),
              nextCellSize,
              sizes.matchBoxW,
              sizes.matchBoxH
            );

            // create lines
            createLinesToNextMatch(
              centeredMatchPos,
              centeredNextMatchPos,
              sizes,
              tempPayload,
              isLaneOnLeft,
              row % 2 === 0 &&
                Math.floor((sizes.numberOfLanes + 1) / 2) !== lane - 1,
              nightMode
            );
          }
          // END OF LINES ***************************

          // MATCH
          addMatchToPayload(
            finalShowdown,
            tempPayload,
            sizes,
            match,
            centeredMatchPos,
            lane,
            row,
            zoomLevel,
            matchBoxClass,
            nightMode,
            mutedUsers,
            isLaneOnLeft,
            debriefMeta.battleId
          );
        }
      }

      setRenderedView(
        <div
          style={{
            position: "relative",
            width: sizes.screenSizeW.toString(10) + "px",
            height: sizes.screenSizeH.toString(10) + "px",
          }}
        >
          {laneBackgrounds}
          {tempPayload}
        </div>
      );
    }
  }, [finalShowdown, nightMode]);

  // Zoom controls
  const handleZoomIn = () => {
    if (zoomLevel > 1 && tournamentRef.current && finalShowdown) {
      // Calculate current center in bracket coordinates
      const { scrollLeft, scrollTop, clientWidth, clientHeight } =
        tournamentRef.current;
      const centerX = scrollLeft + clientWidth / 2;
      const centerY = scrollTop + clientHeight / 2;
      focusPointRef.current = {
        x: centerX / finalShowdown.sizes.screenSizeW,
        y: centerY / finalShowdown.sizes.screenSizeH,
      };
      setIsZooming(true);
      setZoomLevel(zoomLevel - 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel < 4 && tournamentRef.current && finalShowdown) {
      // Calculate current center in bracket coordinates
      const { scrollLeft, scrollTop, clientWidth, clientHeight } =
        tournamentRef.current;
      const centerX = scrollLeft + clientWidth / 2;
      const centerY = scrollTop + clientHeight / 2;
      focusPointRef.current = {
        x: centerX / finalShowdown.sizes.screenSizeW,
        y: centerY / finalShowdown.sizes.screenSizeH,
      };
      setIsZooming(true);
      setZoomLevel(zoomLevel + 1);
    }
  };

  // After zoom, restore scroll to keep the same focus point centered
  useEffect(() => {
    if (focusPointRef.current && tournamentRef.current && finalShowdown) {
      const { x, y } = focusPointRef.current;
      setTimeout(() => {
        if (tournamentRef.current) {
          const newCenterX = x * finalShowdown.sizes.screenSizeW;
          const newCenterY = y * finalShowdown.sizes.screenSizeH;
          tournamentRef.current.scrollTo({
            left: Math.max(
              0,
              newCenterX - tournamentRef.current.clientWidth / 2
            ),
            top: Math.max(
              0,
              newCenterY - tournamentRef.current.clientHeight / 2
            ),
            behavior: "auto",
          });
        }
        focusPointRef.current = null;
        setIsZooming(false);
      }, 0);
    }
  }, [zoomLevel, finalShowdown]);

  // Mouse event handlers for panning
  const handleMouseDown = (e) => {
    if (tournamentRef.current && !isZooming) {
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: tournamentRef.current.scrollLeft,
        scrollTop: tournamentRef.current.scrollTop,
      };
      // Prevent text selection
      document.body.style.userSelect = "none";
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && tournamentRef.current) {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      tournamentRef.current.scrollLeft = dragStartRef.current.scrollLeft - dx;
      tournamentRef.current.scrollTop = dragStartRef.current.scrollTop - dy;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.userSelect = "";
  };

  // Attach/remove mousemove and mouseup listeners globally when dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("mouseleave", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mouseleave", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mouseleave", handleMouseUp);
    };
  }, [isDragging]);

  // Center on the center (finalist) lane on initial load
  useEffect(() => {
    if (finalShowdown && tournamentRef.current && !hasCenteredOnLoad) {
      const sizes = finalShowdown.sizes;
      const centerLane = Math.ceil(sizes.numberOfLanes / 2);
      const centerLaneLeft = (centerLane - 1) * sizes.laneW;
      const centerLaneWidth = sizes.laneW;
      // Center the lane in the viewport (x)
      const container = tournamentRef.current;
      const scrollLeft =
        centerLaneLeft + centerLaneWidth / 2 - container.clientWidth / 2;
      const maxScrollLeft = sizes.screenSizeW - container.clientWidth;

      // --- Use rendering logic for y axis ---
      // Center match in center lane is always row 0
      const cellSize = getCellSizeForLane(sizes, 1);
      const cellPos = getCellPosition(cellSize, 0, centerLane);
      const centeredMatchPos = getCenteredObjectPosition(
        cellPos,
        cellSize,
        sizes.matchBoxW,
        sizes.matchBoxH
      );
      // Center the match box in the viewport, accounting for sticky header
      const matchBoxCenterY = centeredMatchPos.y + sizes.matchBoxH / 2;
      const scrollTop =
        matchBoxCenterY - container.clientHeight / 2 - sizes.headerHeight / 2;
      const maxScrollTop = sizes.screenSizeH - container.clientHeight / 2;

      // Debug log
      // console.log("[Tournament Centering Debug]", {
      //   centeredMatchPosY: centeredMatchPos.y,
      //   matchBoxH: sizes.matchBoxH,
      //   matchBoxCenterY,
      //   clientHeight: container.clientHeight,
      //   scrollHeight: container.scrollHeight,
      //   scrollTop,
      //   maxScrollTop,
      // });

      // Try direct assignment instead of scrollTo
      container.scrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));
      container.scrollTop = Math.max(0, Math.min(scrollTop, maxScrollTop));
      setHasCenteredOnLoad(true);
    }
  }, [finalShowdown, hasCenteredOnLoad]);

  return (
    <div style={{ position: "relative" }}>
      {/* Zoom Controls */}
      <TournamentZoomControls
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        nightMode={nightMode}
      />

      <div
        ref={tournamentRef}
        className={
          "tournament-viewer" + (nightMode ? " tournament-viewer--white" : "")
        }
        style={{
          position: "relative",
          cursor: isDragging ? "grabbing" : "grab",
          WebkitUserSelect: isDragging ? "none" : "auto",
          userSelect: isDragging ? "none" : "auto",
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Sticky Header */}
        {finalShowdown && (
          <TournamentLaneHeader
            totalLanes={finalShowdown.sizes.numberOfLanes}
            sizes={finalShowdown.sizes}
            nightMode={nightMode}
          />
        )}
        {/* End Sticky Header */}
        {renderedView}
        {/* Dummy block to force scroll height */}
        {!hasCenteredOnLoad && finalShowdown?.sizes?.screenSizeH && (
          <div style={{ height: finalShowdown.sizes.screenSizeH }} />
        )}
        {/* Overlay to block interaction during zoom */}
        {isZooming && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255,255,255,0.01)",
              zIndex: 1000,
              pointerEvents: "all",
            }}
          />
        )}
      </div>
      {finalShowdown && (
        <TournamentMiniMap
          sizes={finalShowdown.sizes}
          currentViewport={currentViewport}
          onViewportChange={handleViewportChange}
          nightMode={nightMode}
          zoomLevel={zoomLevel}
        />
      )}
    </div>
  );
}

export default FSTournamentView;
